/**
 * Validator class.
 *
 * How to use it:
 *
 *     new Validator('#form', {
 *         'username': [
 *             { name: 'notBlank', config: { message: 'Username can not be blank' }, enabled: true },
 *             { name: 'regex', config: { pattern: '[a-z]+' }, enabled: true }
 *         ],
 *         'email': [
 *             { name: 'regex', config: { pattern: '[a-z]+ }, enabled: true }
 *         ],
 *         'firstName': {
 *           rules: [
 *             { name: 'notBlank', config: { message: 'First name can not be blank' }, enabled: false }
 *           ],
 *           trim: true
 *         },
 *         'age': [
 *             { name: function(value, resolve, reject, context) {
 *                 if (value > 10) {
 *                     resolve();
 *                 } else {
 *                     reject('Some validation error.');
 *                 }
 *             }}
 *         ]
 *     });
 *
 * Available rules:
 *
 * - notBlank
 * - regex
 * - callback
 * - positiveOrZero
 * - type
 *
 * @todo Add more validation rules.
 * @todo Create documentation.
 * TODO: checkbox that is required to be checked.
 */

import Debounce from '../utils/debounce';
import Prop from '../utils/prop';

export default class Validator {
  /**
   *
   *     new Validator('#form', {
   *         'username': [
   *             { name: 'notBlank' }
   *         ]
   *     })
   *
   * Available options:
   *
   * * scrollToFirstError {bool=false} If validation fails, scroll to first error.
   * * debug {bool=false} Log debug info to console.
   * * addError {function|null} Custom function to set error message to element.
   * * removeError {function|null} Custom function to remove error message to element.
   * * preventFormSubmit {bool=false} Prevent form submit after validation success.
   *
   * @param form
   * @param rules
   * @param options
   */
  constructor(form, rules, options = {}) {
    if (typeof form === 'string') {
      form = document.querySelector(form);
    }

    if (!(form instanceof Node)) {
      throw TypeError('Validator first argument should be valid HTML element.');
    }

    //region Add instance to global accessible property for debugging purpose.
    if (!window.sdValidators) {
      window.sdValidators = [];
    }
    window.sdValidators.push(this);
    //endregion

    this.listeners = {};
    this.form = form;

    this.generalErrorsContainer = options.generalErrorsContainer || 'auto';
    this.scrollToFirstError = options.scrollToFirstError || false;
    this.removeError = options.removeError || null;
    this.addError = options.addError || null;
    this.debug = options.debug || false;
    this.preventFormSubmit = false;

    // Turn off built-in validation.
    form.setAttribute('novalidate', 'true');

    this.formValidators = [];
    this.fields = {};
    for (let fieldName in rules) {
      if (!rules.hasOwnProperty(fieldName)) {
        continue;
      }

      let value = rules[fieldName],
        onlyRules = Array.isArray(value),
        selector;

      if (onlyRules || !value.selector) {
        // If selector is not directly defined (as "selector" config) we treat
        // field name as a selector.
        selector = '[name="' + fieldName + '"]';
      } else {
        selector = value.selector;
      }

      let field = this.getField(fieldName, true);

      if (onlyRules) {
        field.rules = value;
      } else {
        field.rules = value.rules || [];
        delete value.rules;
        field.config = value;
      }

      field.selector = selector;
    }

    this.fieldsIndex = Object.keys(this.fields);
    this.valid = false;

    const formHasSubmitButton = form.querySelector('[type=submit]') !== null;
    let modalSubmitBtn = null;
    if (!formHasSubmitButton) {
      const modal = window.parent.document.querySelector('.modal.show');
      modalSubmitBtn = modal ? modal.querySelector('.modal-confirm') : null
    }

    this.submitBtn = form.querySelector('[type=submit]') || modalSubmitBtn || document.createElement('button');

    this.update();

    this.originalSubmit = form.submit;
    const newSubmit = (e) => {
      const valid = this.valid;
      if (!valid && e) {
        e.preventDefault();
      }
      this.submitForm();
      if (!valid) {
        return false;
      }
    };
    form.submit = newSubmit;
    form.addEventListener('submit', newSubmit);
  }

  /**
   * Add event listener to input based on type.
   *
   * @param {Object} field
   * @return {void}
   * @private
   */
  addFieldChangeEventListener(field) {
    const
      me = this,
      input = field.el,
      name = field.name;

    if (input.hasAttribute('sd-validator-event')) {
      me.addFieldEventListener(name, input.getAttribute('sd-validator-event'));
      return;
    }

    if (input.nodeName.toLowerCase() === 'input' && input.type !== 'checkbox') {
      me.addFieldEventListener(name, 'keyup');
    }

    me.addFieldEventListener(name,'change');
  }

  /**
   * Add keyup event listener to input and run validation rules on change.
   *
   *     keyupEvent(input, field);
   *
   * @param {string} fieldName
   * @param {string} eventName
   * @return {void}
   * @private
   */
  addFieldEventListener(fieldName, eventName) {
    const
      me = this,
      field = this.getField(fieldName),
      input = field.el;

    const debounce = new Debounce((event) => {
      me.callRules(fieldName, field.rules, 0, event.target.value, function (status) {
        if (status !== true) {
          me.setError(fieldName, status);
        } else {
          me.clearError(fieldName);
        }
      });
    }, 800);

    field.elEventListener = debounce.call();

    // Remove previous listener.
    if (field.elEventListener) {
      input.removeEventListener(eventName, field.elEventListener);
    }

    input.addEventListener(eventName, field.elEventListener);

    // Support chosen event to clear errors on change
    if (input.tagName === 'SELECT' && input.classList.contains('chosen-image-select')) {
      $(input).change( field.elEventListener );
    }
  }

  /**
   * Add option to field.
   *
   *     validator.addFieldOption('username', 'trim', true);
   *
   * @param {String} fieldName Field name.
   * @param {String} option Name of the option.
   * @param {*} value Value.
   * @returns {Validator}
   */
  addFieldOption(fieldName, option, value) {
    const field = this.getField(fieldName, true);

    field.config[option] = value;

    return this;
  }

  /**
   * Add field validation.
   *
   *     validator.addFieldValidation('username', 'notBlank', {
   *       message: 'You need to specify your user name.'
   *     });
   *
   *     validator.addFieldValidation('username', (value, resolve, reject) => {
   *       if (value[0] === 'a') {
   *         reject('Your user name can not start with an "a".');
   *       } else {
   *         resolve();
   *       }
   *     });
   *
   * @param {String} fieldName Field name.
   * @param {String/Function} rule
   * @param {object} config Rule configuration object.
   * @returns {Validator}
   */
  addFieldValidation(fieldName, rule, config= {}) {
    const field = this.getField(fieldName, true);

    field.config = Object.assign(field.config, config);
    field.rules.push({
      name: rule,
      config: config,
    });

    return this;
  }

  addFormValidation(fn) {
    this.formValidators.push({
      fn: fn
    });

    return this;
  }

  /**
   * Clear validation error message from the element.
   *
   *     // Clear error from the element.
   *     clearValidationError(element);
   *
   * @param {string} fieldName
   * @return {void}
   */
  clearError(fieldName) {
    // Remove general errors.
    if (!fieldName) {
      if (this.generalErrorsContainer instanceof Node) {
        this.generalErrorsContainer.parentNode.removeChild(this.generalErrorsContainer);
      }
      return;
    }

    const
      field = this.getField(fieldName),
      element = field.el;

    field.errors = [];

    if (element.tagName === 'SELECT'
      && element.classList.contains('chosen-image-select')
      && element.nextSibling.id.indexOf('chosen') > -1
    ) {
      const chosenPicker = element.nextSibling;
      chosenPicker.classList.remove('error');
    }

    const errorContainer = document.querySelector('[sd-validator-error="'+element.id+'"]');

    if (errorContainer) {
      errorContainer.innerHTML = '';
      return;
    }


    const tokenField = field.el.closest('.tokenfield');
    if (tokenField) {
      if (tokenField.nextSibling.nodeName === 'UL') {
        tokenField.nextSibling.innerHTML = '';
      }
      return;
    }

    // Run custom function with removing error message.
    if (this.removeError) {
      this.removeError(element);
      return;
    }

    const errorList = element.parentNode.querySelector(':scope > ul');
    if (errorList) {
      errorList.parentNode.removeChild(errorList);
      return;
    }
  }

  emit(eventName, eventObject = {}) {
    const
      listeners = this.listeners[eventName] || [],
      len = listeners.length;

    for (let i = 0; i < len; i++) {
      listeners[i].fn(eventObject, this);
    }

    return this;
  }

  /**
   * Get field by name.
   * @param {String} fieldName
   * @param {Boolean} [create=false] If not exists, create one.
   * @returns {*}
   */
  getField(fieldName, create = false) {
    const fields = this.fields;
    const prop = new Prop();
    if (!prop.hasOwn(fields, fieldName) && create) {
      fields[fieldName] = {
        name: fieldName,
        selector: fieldName,
        config: {},
        rules: [],
        errors: [],
      };
      // Update fields index.
      this.fieldsIndex = Object.keys(fields);
    }

    if (prop.hasOwn(fields, fieldName)) {
      return fields[fieldName];
    }

    throw TypeError('Field "'+fieldName+'" not found.');
  }

  /**
   * Get element by form name and field name.
   *
   *     getInput('[name="username"]');
   *
   * @param {string} selector
   * @return {Node|null}
   */
  getInput(selector) {
    return this.form.querySelector(selector);
  }

  log() {
    if (this.debug) {
      const args = Array.prototype.slice.call(arguments);
      args[0] = '[Solodev Validator] ' + args[0];
      console.log.apply(null, args);
    }
  }

  on(eventName, callback) {
    const listeners = this.listeners;

    if (!listeners.hasOwnProperty(eventName)) {
      listeners[eventName] = [];
    }

    listeners[eventName].push({
      event: eventName,
      fn: callback
    })

    return this;
  }

  submitForm() {
    const me = this;
    const submitBtn = me.submitBtn;
    submitBtn.setAttribute('disabled', 'disabled');

    me.log('Form submitted.');

    if (!me.valid) {
      me.validate().then(() => {
        me.valid = true;
        if (!this.preventFormSubmit) {
          this.emit('form-submit');
          me.enableAllDisabledInputs(me.form)
          this.originalSubmit.call(me.form);
        }
      }, () => {
        submitBtn.removeAttribute('disabled');

        if (me.scrollToFirstError) {
          const firstError = me.form.querySelector('[sd-error]');
          if (firstError) {
            me.log('Scroll to: ', firstError);
            firstError.scrollIntoView({
              behavior: 'smooth',
              block: 'center'
            });
          }
        }

        me.emit('validation-failed');
      });
      return false;
    }

    return true;
  }

  enableAllDisabledInputs(form) {
    if (!form.classList.contains('allow-disabled-fields')) {
      return;
    }

    const inputs = form.querySelectorAll('input');
    for (const input of [...inputs]) {
      input.disabled = false;
    }
  }

  /**
   * Add change event listeners for form fields.
   *
   *     onChangeEvents(fields);
   *
   * @return {void}
   */
  update() {
    const fields = this.fields;

    for (let fieldName in fields) { // jshint ignore: line
      this.initField(fieldName);
    }
  }

  /**
   * Initialize validator field:
   * - searches for DOM input
   * - adds necessary event listeners
   *
   * This method may be safely called multiple times.
   *
   * @param fieldName
   * @return void
   */
  initField(fieldName) {
    const field = this.getField(fieldName);
    field.el = this.getInput(field.selector);
    if (field.el) {
      this.addFieldChangeEventListener(field);
    }
  }

  /**
   * Run validation for form.
   *
   *     validate(event);
   *
   * @return {Promise}
   */
  validate() {
    const me = this;

    this.log('Validation started.');

    if (me.valid) {
      this.log('Validation finished (ok).');
      return Promise.resolve();
    }

    return me.doValidate();
  }

  /**
   * Run validation for form.
   *
   *     validate();
   *
   * @return {Promise}
   */
  doValidate() {
    return new Promise((resolve, reject) => {
      this.validateFields(0, (status) => {
        if (status) {
          // Clear general errors before validating form.
          this.clearError(null);
          this.validateForm(0, (status) => {
            if (status) {
              this.log('Validation finished (ok).');
              resolve();
            } else {
              this.log('Validation finished (not ok).');
              reject();
            }
          });
        } else {
          this.log('Validation finished (not ok).');
          reject();
        }
      });
    });
  }

  validateForm(index, done) {
    const
      me = this,
      validators = me.formValidators;

    // All validators were executed.
    if (validators.length === index) {
      done(true);
      return;
    }

    validators[index].fn.apply(me, [
      () => {
        me.validateForm(index+1, done);
      },
      (err) => {
        me.setError(null, err);
        done(false);
      }
    ]);
  }

  /**
   * Call validation for fields.
   *
   *     validateFields(fields, 0, done);
   *
   * @param {Number} index
   * @param {function} done
   * @return {void}
   */
  validateFields(index, done) {
    const me = this,
      fieldsIndex = me.fieldsIndex,
      fields = me.fields;

    if (fieldsIndex.length === index) {
      done(true);
      return;
    }

    const
      field = fields[fieldsIndex[index]],
      fieldName = field.name;

    if (!field.el) {
      me.validateFields(index+1, done);
      return;
    }

    me.validateField(fieldName).then(
      () => {
        me.clearError(fieldName);
        me.validateFields(index + 1, done);
      }, (err) => {
        me.setError(fieldName, err);
        me.validateFields(index + 1, () => {});
        done(false);
      }
    );
  }

  /**
   * Call validation for field.
   *
   *     validateField(fieldName);
   *
   * @param {String} fieldName
   * @return {Promise}
   */
  validateField(fieldName) {
    const field = this.getField(fieldName);

    return new Promise((resolve, reject) => {
      let value = this.getValue(field.el);

      if (field.config.trim) {
        value = value.trim();
      }

      this.log('Validating "'+field.selector+'".');

      this.callRules(
        field.name,
        field.rules,
        0,
        value,
        (status) => {
          status === true ? resolve() : reject(status); // jshint ignore: line
        }
      );
    });
  }

  /**
   * Call validation rules on input element.
   *
   *     callRules(rules, 0, input, value, done);
   *
   * @param {String} fieldName
   * @param {Array} rules
   * @param {Number} index
   * @param {String} value
   * @param {Function} done
   * @return {void}
   */
  callRules(fieldName, rules, index, value, done) {
    const me = this;

    if (rules.length === index) {
      done(true);
      return;
    }

    me.callRule(fieldName, rules[index], value).then(
      () => {
        me.clearError(fieldName);
        me.callRules(fieldName, rules, index + 1, value, done);
      }, (err) => {
        done(err || rules[index].rule.config.message);
      }
    );
  }

  /**
   * Call validation rule on input element.
   *
   *     callRule(rule, input, value);
   *
   * @param {string} fieldName
   * @param {Object} rule
   * @param {string} value
   * @return {Promise}
   */
  callRule(fieldName, rule, value) {
    this.log('Executing validation rule: "'+(typeof rule.name === 'function' ? 'custom function' : rule.name)+'".');

    const field = this.getField(fieldName);

    return new Promise((resolve, reject) => {
      const context = {
        field: field,
        input: field.el,
        rule: rule
      };

      if (rule.hasOwnProperty('enabled') && !rule.enabled) {
        return resolve();
      }

      if (typeof rule.name === 'function') {
        rule.name(value, resolve, reject, context);
        return;
      }

      if (!Validator.rules.hasOwnProperty(rule.name)) {
        throw TypeError('Validation rule "' + rule.name + '" is not defined.');
      }

      Object.assign(context, rule);
      delete context.name;
      Validator.rules[rule.name](value, resolve, reject, context);
    });
  }

  /**
   * Turn on/off field validation rule.
   * @param {String} fieldName
   * @param {String} [ruleName]
   * @param {Boolean|String} status
   * @return {*}
   */
  setFieldValidationStatus(fieldName, ruleName, status) {
    if (arguments.length === 2) {
      status = ruleName;
      ruleName = null;
    }

    const field = this.getField(fieldName);
    field.rules.map(r => {
      if (!ruleName || r.name === ruleName) {
        r.enabled = status;
      }

      return r;
    })
    return this;
  }

  /**
   * Return field value.
   * @param {String} fieldName
   * @return {string|null}
   */
  getFieldValue(fieldName) {
    return this.getValue(this.getField(fieldName).el);
  }

  /**
   * Get input value.
   *
   *     getValue(input);
   *
   * @param {HTMLInputElement} input
   * @return {string|null}
   */
  getValue(input) {
    switch (input.nodeName.toLowerCase()) {
      case 'select':
        const selected = [];
        for (let i = 0; i < input.options.length; i++) {
          var opt = input.options[i];
          if (input.nodeType === 1 && opt.selected) {
            selected.push(opt.value);
          }
        }

        if (input.multiple) {
          return selected;
        } else {
          return selected.length ? selected[0] : null;
        }
        break;
      case 'textarea':
      case 'input':
        if (input.type === 'checkbox') {
          return input.checked;
        }
        return input.value;
    }

    throw Error('Unrecognized input type.');
  }

  /**
   * Return true if specified field name has any validation errors.
   *
   * @param {String} fieldName
   * @return {boolean}
   */
  hasError(fieldName) {
    return this.getField(fieldName).errors.length !== 0;
  }

  /**
   * Remove all validation rules from the field.
   * @param {String} fieldName
   */
  removeFieldValidation(fieldName) {
    this.fields[fieldName].rules = []
  }

  removeField(fieldName) {
    delete this.fields[fieldName];
  }

  /**
   * Remove all validation references from the field.
   * @param {String} fieldName
   */
  clearFieldValidations(fieldName) {
    this.log('Remove fields from validations: ', fieldName);

    const
      field = this.getField(fieldName),
      input = field.el;

    // Remove field from validation.
    delete this.fields[fieldName];

    // Remove field from fields list.
    this.fieldsIndex = this.fieldsIndex.filter(f => f !== fieldName);

    // Remove all event listeners from the field.
    const new_element = input.cloneNode(true);
    input.parentNode.replaceChild(new_element, input);
  }

  /**
   * Set validation error message on element.
   *
   *     Add 'error' class to the element.
   *     If element has an error, function will change the error message.
   *     If element doesn't have an error, function will create
   *     <ul> with one <li>, which will contain error message.
   *     // Add 'This fields is required.' error message to the element.
   *     setValidationError(element, 'This fields is required.');
   *
   * If element with attribute `sd-validator-error` exists with value same as input's id
   * then error will be rendered into such element.
   *
   * @param {String/null} fieldName
   * @param {String} message
   * @return {void}
   */
  setError(fieldName, message) {
    this.log('Set error: ' + message, fieldName);

    if (fieldName) {
      const
        field = this.getField(fieldName),
        element = field.el;

        if (element.tagName === 'SELECT'
          && element.classList.contains('chosen-image-select')
          && element.nextSibling.id.indexOf('chosen') > -1
        ) {
          const chosenPicker = element.nextSibling;
          chosenPicker.classList.add('error');
        }

      field.errors.push(message);

      const errorContainer = document.querySelector('[sd-validator-error="' + element.id + '"]');

      if (errorContainer) {
        errorContainer.innerHTML = '<ul class="errors-list"><li>' + message + '</li></ul>';
        return;
      }

      const tokenField = field.el.closest('.tokenfield');
      if (tokenField) {
        const nextElement = tokenField.nextSibling;
        const errorTemplate = '<ul class="errors-list"><li>' + message + '</li></ul>';

        if (nextElement.nodeName === 'UL') {
          nextElement.innerHTML = errorTemplate;
        } else {
          const template = document.createElement('template');
          template.innerHTML = errorTemplate;
          const node = template.content.firstElementChild;
          tokenField.parentNode.insertBefore(node, tokenField.nextSibling);
        }
        return;
      }

      // Run custom function with adding error message.
      if (this.addError) {
        this.addError(element, message);
      }
    } else {
      // General error.
      let errorsContainer;
      if (this.generalErrorsContainer === 'auto') {
        errorsContainer = document.createElement('div');
        this.form.insertBefore(errorsContainer, this.form.firstChild);
        this.generalErrorsContainer = errorsContainer;
      }

      errorsContainer = this.form.getAttribute('sd-validator-general-errors') || this.generalErrorsContainer;

      if (typeof errorsContainer === 'string') {
        errorsContainer = document.querySelector(errorsContainer);
      }

      if (!(errorsContainer instanceof Node)) {
        this.log('Can not display general errors because no general errors container set up.');
      }

      errorsContainer.innerHTML = '<div class="errors-general"><p>'+message+'</p></div>';
    }
  }

  /**
   * Set flag if form has own submit handler attached.
   *
   * @param {boolean} value
   * @return {void}
   */
  setPreventFormSubmit(value) {
    this.preventFormSubmit = value;
  }
}

Validator.tmpl = (str, replacements) => {
  return str.replace(/\{\{\s{1,}([a-zA-Z0-9-_]+)\s{1,}\}\}/g, function (match, group) {
    return replacements[group];
  });
};

Validator.defaults = {
  url: '/^(https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|www\.[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9]+\.[^\s]{2,}|www\.[a-zA-Z0-9]+\.[^\s]{2,})$/',
  email: /^[a-zA-Z0-9.!#$%&'*+\\/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+$/
};

Validator.rules = {
  regex: (value, resolve, reject, context) => {
    const pattern = new RegExp(context.rule.config.pattern);
    pattern.test(value) ? resolve() : reject(context.rule.config.message); // jshint ignore: line
  },
  notBlank: (value, resolve, reject, context) => {
    !!value ? resolve() : reject(context.rule.config.message); // jshint ignore: line
  },
  length: (value, resolve, reject, context) => {
    if (!value || typeof value !== 'string') {
      resolve();
      return;
    }
    const
      config = context.rule.config,
      len = value.length;

    if (config.max && len > config.max) {
      const msg = Validator.tmpl(config.maxMessage, {
        value: value,
        limit: config.max
      });
      reject(msg);
    }

    if (config.min && len < config.min) {
      const msg = Validator.tmpl(config.minMessage, {
        value: value,
        limit: config.min
      });
      reject(msg);
      return;
    }

    resolve();
  },
  // Differences with Symfony's PositiveOrZero constraint:
  // - does not support DateTimes.
  // - does not support "compared_value_type" property.
  positiveOrZero: (value, resolve, reject, context) => {
    if (!value || isNaN(parseFloat(value))) {
      resolve();
      return;
    }

    const config = context.rule.config,
        ruleValue = config.value || 0;
    if (parseFloat(value) < parseFloat(ruleValue)) {
      const msg = Validator.tmpl(config.message, {
        value: value,
        compared_value: ruleValue
      });
      reject(msg);
      return;
    }

    resolve();
  },
  greaterThanOrEqual: (value, resolve, reject, context) => {
    if (!value || isNaN(parseFloat(value))) {
      resolve();
      return;
    }

    const
      config = context.rule.config,
      ruleValue = config.value || 0;
    if (parseFloat(value) < parseFloat(ruleValue)) {
      const msg = Validator.tmpl(config.message, {
        value: value,
        compared_value: ruleValue
      });
      reject(msg);
      return;
    }

    resolve();
  },
  lessThanOrEqual: (value, resolve, reject, context) => {
    if (!value || isNaN(parseFloat(value))) {
      resolve();
      return;
    }

    const
      config = context.rule.config,
      ruleValue = config.value || 0;
    if (parseFloat(value) > parseFloat(ruleValue)) {
      const msg = Validator.tmpl(config.message, {
        value: value,
        compared_value: ruleValue
      });
      reject(msg);
      return;
    }

    resolve();
  },
  // Differences with Symfony's Type constraint:
  // - supports only float, double, numeric, int/integer, boolean
  type: (value, resolve, reject, context) => {
    if (!value || typeof value !== 'string') {
      resolve();
      return;
    }

    const config = context.rule.config;
    let
      types = config.type,
      ok = false;

    if (!Array.isArray(types)) {
      types = [types];
    }

    types.forEach((type) => {
      type = type.toLowerCase();
      switch (type) {
        case 'boolean':
        case 'string':
          ok = true;
          break;
        case 'float':
        case 'double':
        case 'numeric':
          if (/^[+-]?\d+(\.\d+)?$/.test(value)) {
            ok = true;
          }
          break;
        case 'int':
        case 'integer':
          if (/^\d+$/.test(value)) {
            ok = true;
          }
          break;
      }
    });

    if (ok) {
      resolve();
    } else {
      const msg = Validator.tmpl(config.message, {
        value: value,
        type: config.type
      });
      reject(msg);
    }
  },
  email: (value, resolve, reject, context) => {
    if (!value || typeof value !== 'string') {
      resolve();
      return;
    }

    const pattern = new RegExp(Validator.defaults.email);
    if (pattern.test(value)) {
      resolve();
    } else {
      const config = context.rule.config;
      reject(Validator.tmpl(config.message, {
        value: value
      }));
    }
  },
  url: (value, resolve, reject, context) => {
    if (!value || typeof value !== 'string') {
      resolve();
      return;
    }

    const pattern = new RegExp(context.rule.config.pattern || Validator.defaults.url);
    if (pattern.test(value)) {
      resolve();
    } else {
      const config = context.rule.config;
      reject(Validator.tmpl(config.message, {
        value: value
      }));
    }
  },
  choice: (value, resolve, reject, context) => {
    if (!value || typeof value !== 'string') {
      resolve();
      return;
    }

    const config = context.rule.config;

    if (config.choices.indexOf(value) === -1) {
      reject(config.message);
    } else {
      resolve();
    }
  },
  json: (value, resolve, reject, context) => {
    if (!value || typeof value !== 'string') {
      resolve();
      return;
    }

    try {
      JSON.parse(value);
      resolve();
    } catch (e) {
      reject(context.rule.config);
    }
  },
  dateTime: (value, resolve, reject, context) => {
    if (!value || typeof value !== 'string') {
      resolve();
      return;
    }

    if (/(\d{4})-(\d{2})-(\d{2}) (\d{2}):(\d{2})/.test(value)) {
      resolve();
    } else {
      reject(context.rule.config.message);
    }
  },
  // Supports only numbers.
  range: (value, resolve, reject, context) => {
    if (!value || isNaN(parseFloat(value))) {
      resolve();
      return;
    }

    const
      config = context.rule.config,
      min = config.min,
      max = config.max;

    value = parseFloat(value)

    if (min && value < min) {
      const msg = Validator.tmpl(config.minMessage, {
        value: value,
        limit: min
      });
      reject(msg);
      return;
    }

    if (max && value > max) {
      const msg = Validator.tmpl(config.maxMessage, {
        value: value,
        limit: max
      });
      reject(msg);
      return;
    }

    resolve();
  }
};
